import React from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from 'components/ui/panel';
import { Skeleton } from 'components/ui/skeleton';
import { formatGroupedNumber } from 'lib/dateFormat';
import type { OverviewResponse, TestsResponse } from 'modules/analytics';
import { AdvancedUpgradeNotice } from './AdvancedUpgradeNotice';

interface Props {
  data?: OverviewResponse;
  isLoading: boolean;
  hasAdvanced: boolean;
  funnel?: TestsResponse['funnel'];
  funnelLoading: boolean;
}

/**
 * Pass/fail split (overview pass rate) over the completion funnel (PRO tests
 * endpoint). The API returns a pass rate, not pass/fail counts, so the split
 * reads in percent.
 */
export const OutcomesPanel: React.FC<Props> = ({ data, isLoading, hasAdvanced, funnel, funnelLoading }) => {
  const { t } = useTranslation();
  const graded = data && data.completedAttempts > 0 ? data : undefined;
  const passRate = graded?.passRate ?? 0;
  const top = funnel?.[0]?.count ?? 0;

  const renderFunnel = () => {
    if (!hasAdvanced) return <AdvancedUpgradeNotice className="py-5" />;
    if (funnelLoading) {
      return (
        <div className="space-y-3 py-1">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-3.5" />
          ))}
        </div>
      );
    }
    if (!funnel || top === 0) {
      return <p className="py-2 text-[13px] text-muted-foreground">{t('no_activity_this_week')}</p>;
    }
    return (
      <ul>
        {funnel.map((step) => (
          <li
            key={step.stage}
            data-funnel-stage={step.stage}
            className="grid h-8 grid-cols-[96px_minmax(0,1fr)_56px] items-center gap-x-3 text-[13px]"
          >
            <span className="truncate text-foreground/80">{t(`analytics_funnel_${step.stage}`)}</span>
            <span aria-hidden className="h-2 overflow-hidden rounded-full bg-muted">
              <span
                className="block h-full rounded-full bg-foreground"
                style={{ width: `${Math.round((step.count / top) * 100)}%` }}
              />
            </span>
            <span className="text-right tabular-nums text-foreground">{formatGroupedNumber(step.count)}</span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Panel title={t('results')} aside={graded ? <span className="tabular-nums">{`${passRate}% ${t('pass')}`}</span> : undefined}>
      {isLoading ? (
        <div className="space-y-2.5">
          <Skeleton className="h-3.5 w-1/3" />
          <Skeleton className="h-2.5" />
        </div>
      ) : graded ? (
        <>
          <div className="flex items-baseline justify-between gap-3 text-[13px]">
            <span className="text-foreground/80">{t('pass_fail_breakdown')}</span>
            <span className="tabular-nums text-muted-foreground">
              <span className="font-semibold text-success-700 dark:text-success-400">{passRate}%</span>
              {' · '}
              <span className="font-semibold text-error-700 dark:text-error-400">{100 - passRate}%</span>
            </span>
          </div>
          <div
            role="img"
            aria-label={`${passRate}% ${t('pass')}`}
            data-pass-split
            className="mt-2.5 flex h-2.5 overflow-hidden rounded-full bg-muted"
          >
            <span className="bg-success-600" style={{ width: `${passRate}%` }} />
            <span className="bg-error-400" style={{ width: `${100 - passRate}%` }} />
          </div>
        </>
      ) : (
        <p className="text-[13px] text-muted-foreground">{t('analytics_no_graded')}</p>
      )}
      <div className="mt-[18px] border-t border-border pt-3.5">
        <span className="mb-1.5 block text-[13px] text-foreground/80">{t('completion_funnel')}</span>
        {renderFunnel()}
      </div>
    </Panel>
  );
};

export default OutcomesPanel;
